<template>
  <div
    ref="editorRef"
    class="widget-field-base-content-editor"
  />
</template>

<script lang="ts" setup>
import { Editor } from '@tiptap/core'
import { CharacterCount } from '@tiptap/extension-character-count'
import { Document } from '@tiptap/extension-document'
import { Text } from '@tiptap/extension-text'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Placeholder } from '@tiptap/extension-placeholder'
import { HardBreak } from '@tiptap/extension-hard-break'
import { EmojiNode } from '@/services/forms/tmTiptap/extensions/TmEmojiExtension'
import { onBeforeUnmount, onMounted, ref, watch } from '@/composition/vue/compositionApi'

import type { Emoji } from '@/services/forms/tmTiptap/types'

const props = defineProps<{
  modelValue: string
  placeholder?: string
  maxLength?: number
  isDisabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'paste-files', files: File[]): void
}>()

const editorRef = ref<HTMLElement>()
let editorInstance: Editor

const getContent = () => {
  return editorInstance.getText({
    blockSeparator: '\n',
  })
}

const setContent = (content = '') => {
  if (content === getContent()) return
  editorInstance.commands.setContent(content)
}
watch(() => props.modelValue, setContent)

const setPlaceholder = (placeholder?: string) => {
  const placeholderExt = editorInstance.extensionManager.extensions.find(
    (ext) => ext.name === 'placeholder',
  ) as typeof Placeholder
  placeholderExt.options.placeholder = placeholder || ''
  placeholderExt.options.showOnlyWhenEditable = false
  // https://github.com/ueberdosis/tiptap/issues/1935
  editorInstance.view.dispatch(editorInstance.state.tr)
}
watch(() => props.placeholder, setPlaceholder)

const setMaxLength = (maxLength?: number) => {
  const charCountExt = editorInstance.extensionManager.extensions.find(
    (ext) => ext.name === 'characterCount',
  ) as typeof CharacterCount
  charCountExt.options.limit = maxLength
}
watch(() => props.maxLength, setMaxLength)

const setEditable = (editable: boolean) => {
  editorInstance.setEditable(editable)
}
watch(
  () => props.isDisabled,
  (v) => setEditable(!v),
)

const insertEmoji = (emoji: Emoji) => {
  editorInstance.chain().focus().insertEmoji(emoji).run()
}

const focus = () => {
  editorInstance.commands.focus()
}

onMounted(() => {
  editorInstance = new Editor({
    element: editorRef.value,
    content: props.modelValue,
    extensions: [Document, Text, Paragraph, EmojiNode, CharacterCount, Placeholder, HardBreak],
    onCreate: () => {
      setPlaceholder(props.placeholder)
      setMaxLength(props.maxLength)
      setEditable(!props.isDisabled)
    },
    onUpdate: () => {
      emit('update:modelValue', getContent())
    },
    editorProps: {
      handleDOMEvents: {
        keydown: (_, event) => {
          if (event.key === 'Enter' && !event.shiftKey) return true
          return undefined
        },
      },
      handlePaste: (view, event) => {
        const files = event.clipboardData?.files || []
        if (files.length) {
          emit('paste-files', [...files])
          return true
        }
        return false
      },
    },
  })
})

onBeforeUnmount(() => {
  editorInstance.destroy()
})

defineExpose({
  focus,
  insertEmoji,
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

.widget-field-base-content-editor {
  :deep(.ProseMirror) {
    @include thumb-without-track;
    overflow: auto;
    outline: 0;
    height: 100%;
    font-size: 14px;
    line-height: 20px;

    .is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      display: block;
      height: 0;
      color: $light;
      overflow: visible;
      pointer-events: none;
    }

    p {
      margin: 0;
      padding-bottom: 1px; // solves the problem with screen scaling (subpixel rendering)
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      overflow: hidden;
      word-break: break-word;
    }
  }
}
</style>
